import { StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { store, persistor } from './store'
import { PersistGate } from 'redux-persist/integration/react'
import App from './App'
import { MantineProvider } from '@mantine/core'
import enTranslations from '@shopify/polaris/locales/en.json'
import { AppProvider } from '@shopify/polaris'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@shopify/polaris/build/esm/styles.css'
import './index.css'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import './styles/globals.css'
import { Toaster } from './components/ui/toaster'
import { ErrorBoundary } from 'react-error-boundary'
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from './components/ui/card'
import { CreateSurveyButton } from './features/survey-builder/Tabs/CustomButton'
import { PostHogProvider } from 'posthog-js/react'
import { RefreshCw } from 'lucide-react'
import BreakingLogic from './assets/builder/screens/BreakingLogic'

// Type definitions for error types
type ErrorType = 'network' | 'application' | 'unknown'

interface ErrorInfo {
    type: ErrorType
    message: string
    details?: string
}

// Utility function to determine error type
function determineErrorType(error: Error): ErrorInfo {
    const errorMessage = error.message.toLowerCase()

    // Check for network-related errors (exclude TypeError/ReferenceError to
    // avoid misclassifying runtime crashes as network issues — stack traces
    // from lazy-loaded chunks can contain "fetch" and cause false positives)
    if (
        !(error instanceof TypeError) &&
        !(error instanceof ReferenceError) &&
        (
            errorMessage.includes('network') ||
            errorMessage.includes('fetch') ||
            errorMessage.includes('connection') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('cors') ||
            !navigator.onLine
        )
    ) {
        return {
            type: 'network',
            message: 'Network connection issue detected',
            details: error.message,
        }
    }

    // Check for common React/JavaScript application errors
    if (
        errorMessage.includes('chunk') ||
        errorMessage.includes('loading css chunk') ||
        errorMessage.includes('loading js chunk') ||
        (error.stack?.toLowerCase() || '').includes('chunkloaderror')
    ) {
        return {
            type: 'application',
            message: 'Application loading error',
            details:
                'There was an issue loading part of the application. This might be due to a recent update.',
        }
    }

    // Default to application error for other React errors
    return {
        type: 'application',
        message: 'Application error occurred',
        details: error.message,
    }
}

// Network Error Fallback Component (Original)
function NetworkErrorFallback({
    resetErrorBoundary,
}: {
    resetErrorBoundary: () => void
}) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#fafbfa] p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="flex flex-col items-center">
                    <div className="mb-6">
                        <BreakingLogic />
                    </div>
                    <CardTitle className="text-center">
                        Whoops! It looks like you're offline
                    </CardTitle>
                    <CardDescription className="text-center">
                        Please ensure you have a stable internet connection and
                        try again.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex justify-center">
                    <CreateSurveyButton
                        text="Try again"
                        onClick={resetErrorBoundary}
                    />
                </CardContent>
            </Card>
        </div>
    )
}

// Application Error Fallback Component
function ApplicationErrorFallback({
    error,
    resetErrorBoundary,
    errorInfo,
}: {
    error: Error
    resetErrorBoundary: () => void
    errorInfo: ErrorInfo
}) {
    const [showDetails, setShowDetails] = useState(false)

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#fafbfa] p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="flex flex-col items-center">
                    <div className="mb-4">
                        <BreakingLogic />
                    </div>
                    <CardTitle className="text-center">
                        Oops! Something went wrong
                    </CardTitle>
                    <CardDescription className="text-center">
                        {errorInfo.details ||
                            'An unexpected error occurred in the application. Please try refreshing the page.'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 ">
                    <div className="flex items-center justify-center gap-2">
                        <CreateSurveyButton
                            icon={<RefreshCw className="h-4 w-4" />}
                            text="Refresh Page"
                            onClick={resetErrorBoundary}
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

// Enhanced Error Fallback that routes to appropriate component
function EnhancedErrorFallback({
    error,
    resetErrorBoundary,
}: {
    error: Error
    resetErrorBoundary: () => void
}) {
    const errorInfo = determineErrorType(error)

    // Log error for debugging (you might want to send this to your error tracking service)
    console.error('Error Boundary caught an error:', {
        type: errorInfo.type,
        message: error.message,
        stack: error.stack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        online: navigator.onLine,
    })

    if (errorInfo.type === 'network') {
        return <NetworkErrorFallback resetErrorBoundary={resetErrorBoundary} />
    }

    return (
        <ApplicationErrorFallback
            error={error}
            resetErrorBoundary={resetErrorBoundary}
            errorInfo={errorInfo}
        />
    )
}

// QueryClient is now imported from @/lib/queryClient (shared instance)

const options = {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    autocapture: false,
}

createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <PostHogProvider
            apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
            options={options}
        >
            <ErrorBoundary
                FallbackComponent={EnhancedErrorFallback}
                onReset={() => {
                    // Clear any cached data that might be causing issues
                    if ('caches' in window) {
                        caches.keys().then((names) => {
                            names.forEach((name) => caches.delete(name))
                        })
                    }
                    window.location.reload()
                }}
                onError={(error, errorInfo) => {
                    // You can integrate with error tracking services here
                    // Example: Sentry, LogRocket, etc.
                    console.error('Error Boundary Error:', error, errorInfo)
                }}
            >
                <AppProvider i18n={enTranslations}>
                    <QueryClientProvider client={queryClient}>
                        <Provider store={store}>
                            <PersistGate loading={null} persistor={persistor}>
                                <BrowserRouter>
                                    <MantineProvider>
                                        <App />
                                        <Toaster />
                                    </MantineProvider>
                                </BrowserRouter>
                            </PersistGate>
                        </Provider>
                    </QueryClientProvider>
                </AppProvider>
            </ErrorBoundary>
        </PostHogProvider>
    </StrictMode>
)
